import { findInlineMatch } from "@/lib/richFormat";

// Lot 778 — aller-retour entre le texte à repères des publications (**gras**, *italique*, __souligné__, « - » puces,
// « > » citation, « \n » lignes) et le document Tiptap/ProseMirror du champ de publication (aperçu en direct).
// La grammaire de lecture est celle de components/RichText.jsx (findInlineMatch) : ce que l'éditeur montre = la carte du fil.

const MARK_ORDER = ["bold", "italic", "underline"];           // du plus extérieur au plus intérieur : ***__x__***
const MARKERS = { bold: "**", italic: "*", underline: "__" };
const isQuote = (l) => /^\s*>\s?/.test(l);
const isBullet = (l) => /^\s*-\s+/.test(l);

const textNode = (text, marks) => (text ? { type: "text", text, ...(marks.length ? { marks: marks.map((type) => ({ type })) } : {}) } : null);

function inlineNodes(text, marks = []) {
  const out = [];
  let rest = text;
  while (rest) {
    const m = findInlineMatch(rest);
    if (!m) { out.push(textNode(rest, marks)); break; }
    if (m.start > 0) out.push(textNode(rest.slice(0, m.start), marks));
    const inner = rest.slice(m.start + m.rule.open.length, m.close);
    out.push(...inlineNodes(inner, marks.includes(m.rule.kind) ? marks : [...marks, m.rule.kind]));
    rest = rest.slice(m.close + m.rule.close.length);
  }
  return out.filter(Boolean);
}

const paragraph = (line) => { const c = inlineNodes(line); return c.length ? { type: "paragraph", content: c } : { type: "paragraph" }; };

export function markersToDoc(text) {
  const lines = String(text || "").split("\n");
  const content = [];
  let i = 0;
  while (i < lines.length) {
    if (isQuote(lines[i])) {
      const q = [];
      while (i < lines.length && isQuote(lines[i])) { q.push(paragraph(lines[i].replace(/^\s*>\s?/, ""))); i += 1; }
      content.push({ type: "blockquote", content: q });
    } else if (isBullet(lines[i])) {
      const items = [];
      while (i < lines.length && isBullet(lines[i])) { items.push({ type: "listItem", content: [paragraph(lines[i].replace(/^\s*-\s+/, ""))] }); i += 1; }
      content.push({ type: "bulletList", content: items });
    } else {
      content.push(paragraph(lines[i]));
      i += 1;
    }
  }
  return { type: "doc", content: content.length ? content : [{ type: "paragraph" }] };
}

// Texte d'un paragraphe : les repères n'encadrent jamais une espace (règle de lecture) → les espaces de bord
// sortent des repères ; à chaque changement de jeu de marques, on referme tout puis on rouvre.
function serializeInline(nodes) {
  let out = "";
  let open = [];
  let pendingWs = "";
  const closeAll = () => { for (let i = open.length - 1; i >= 0; i -= 1) out += MARKERS[open[i]]; open = []; };
  const openAll = (marks) => { open = MARK_ORDER.filter((k) => marks.includes(k)); open.forEach((k) => { out += MARKERS[k]; }); };
  for (const n of nodes || []) {
    if (n.type === "hardBreak") { closeAll(); out += `${pendingWs}\n`; pendingWs = ""; continue; }
    if (n.type !== "text" || !n.text) continue;
    const marks = (n.marks || []).map((m) => m.type).filter((k) => MARKERS[k]);
    const lead = n.text.match(/^\s*/)[0];
    const trail = n.text.match(/\s*$/)[0];
    const core = n.text.slice(lead.length, n.text.length - trail.length);
    if (!core) { pendingWs += n.text; continue; }
    const same = marks.length === open.length && marks.every((k) => open.includes(k));
    if (!same) { closeAll(); out += pendingWs + lead; openAll(marks); } else out += pendingWs + lead;
    out += core;
    pendingWs = trail;
  }
  closeAll();
  return out + pendingWs;
}

const blockText = (node) => (node?.type === "paragraph" ? serializeInline(node.content) : (node?.content || []).map(blockText).join("\n"));

export function docToMarkers(doc) {
  const lines = [];
  for (const b of doc?.content || []) {
    if (b.type === "blockquote") (b.content || []).forEach((p) => blockText(p).split("\n").forEach((l) => lines.push(`> ${l}`)));
    else if (b.type === "bulletList") (b.content || []).forEach((li) => lines.push(`- ${blockText(li).split("\n").join(" ")}`));
    else lines.push(blockText(b));
  }
  return lines.join("\n").replace(/\n+$/, "");   // paragraphes vides de fin (Entrée) : rien à garder
}
